import { useState, useCallback, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import { RefreshCw, Plus, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useProjectSocket } from '../hooks/useSocket';

import IssueStatusPieWidget from '../components/dashboard/IssueStatusPieWidget';
import CreatedVsResolvedWidget from '../components/dashboard/CreatedVsResolvedWidget';
import PriorityBarWidget from '../components/dashboard/PriorityBarWidget';
import AssigneeWorkloadWidget from '../components/dashboard/AssigneeWorkloadWidget';
import BurndownWidget from '../components/dashboard/BurndownWidget';
import TestExecutionDonutWidget from '../components/dashboard/TestExecutionDonutWidget';
import RequirementCoverageWidget from '../components/dashboard/RequirementCoverageWidget';
import RecentActivityWidget from '../components/dashboard/RecentActivityWidget';

import 'react-grid-layout/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface ProjectDetail {
  id: string;
  name: string;
  projectKey: string;
  projectType: string;
  createdAt: string;
}

// 위젯 정의
const WIDGET_DEFS = [
  { id: 'status-pie', name: '작업 상태 분포', defaultW: 1, defaultH: 2 },
  { id: 'created-vs-resolved', name: '생성 vs 해결 추이', defaultW: 2, defaultH: 2 },
  { id: 'priority-bar', name: '우선순위별 작업', defaultW: 1, defaultH: 2 },
  { id: 'assignee-workload', name: '담당자별 작업 현황', defaultW: 1, defaultH: 2 },
  { id: 'burndown', name: '스프린트 번다운', defaultW: 1, defaultH: 2 },
  { id: 'test-execution', name: '테스트 실행 현황', defaultW: 1, defaultH: 2 },
  { id: 'req-coverage', name: '요구사항 커버리지', defaultW: 1, defaultH: 2 },
  { id: 'activity', name: '최근 활동', defaultW: 3, defaultH: 2 },
];

/** 프로젝트 타입별 제외 위젯 */
const EXCLUDED_WIDGETS: Record<string, Set<string>> = {
  CS: new Set(['burndown', 'test-execution', 'req-coverage']),
  TEAM: new Set(['test-execution', 'req-coverage']),
};

function getAvailableWidgets(projectType: string) {
  const excluded = EXCLUDED_WIDGETS[projectType];
  if (excluded) {
    return WIDGET_DEFS.filter((w) => !excluded.has(w.id));
  }
  return WIDGET_DEFS;
}

const DEFAULT_LAYOUT = [
  { i: 'status-pie', x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'created-vs-resolved', x: 1, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
  { i: 'priority-bar', x: 0, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'assignee-workload', x: 1, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'test-execution', x: 2, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'burndown', x: 0, y: 4, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'req-coverage', x: 1, y: 4, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'activity', x: 0, y: 6, w: 3, h: 2, minW: 1, minH: 2 },
];

/** CS 프로젝트 전용 기본 레이아웃 (이슈 중심) */
const CS_DEFAULT_LAYOUT = [
  { i: 'status-pie', x: 0, y: 0, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'created-vs-resolved', x: 1, y: 0, w: 2, h: 2, minW: 1, minH: 2 },
  { i: 'priority-bar', x: 0, y: 2, w: 1, h: 2, minW: 1, minH: 2 },
  { i: 'assignee-workload', x: 1, y: 2, w: 2, h: 2, minW: 1, minH: 2 },
  { i: 'activity', x: 0, y: 4, w: 3, h: 2, minW: 1, minH: 2 },
];

function getDefaultLayout(projectType: string) {
  return projectType === 'CS' ? CS_DEFAULT_LAYOUT : DEFAULT_LAYOUT;
}

function getStorageKey(projectId: string) {
  return `maios-dashboard-layout-${projectId}`;
}

function getWidgetStorageKey(projectId: string) {
  return `maios-dashboard-widgets-${projectId}`;
}

export default function ProjectDashboardPage() {
  const { project } = useOutletContext<{ project: ProjectDetail }>();
  const queryClient = useQueryClient();

  // Socket.io 프로젝트 구독
  useProjectSocket(project.id);

  // 프로젝트 유형에 따른 사용 가능 위젯
  const availableWidgets = useMemo(() => getAvailableWidgets(project.projectType), [project.projectType]);
  const availableIds = useMemo(() => new Set(availableWidgets.map((w) => w.id)), [availableWidgets]);

  // 표시할 위젯 목록 (프로젝트 유형에서 허용된 위젯만)
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(getWidgetStorageKey(project.id));
      if (saved) {
        const parsed = JSON.parse(saved) as string[];
        // 프로젝트 유형에서 허용되지 않는 위젯 제거
        const filtered = parsed.filter((id) => availableIds.has(id));
        if (filtered.length > 0) return filtered;
      }
    } catch { /* ignore */ }
    return availableWidgets.map((w) => w.id);
  });

  // 위젯 추가 드롭다운
  const [showAddMenu, setShowAddMenu] = useState(false);

  // 레이아웃 저장/복원
  const [layouts, setLayouts] = useState(() => {
    try {
      const saved = localStorage.getItem(getStorageKey(project.id));
      if (saved) {
        const parsed = JSON.parse(saved);
        // 저장된 레이아웃에서 허용되지 않는 위젯 제거
        if (parsed.lg) {
          parsed.lg = (parsed.lg as Array<{ i: string }>).filter((l) => availableIds.has(l.i));
        }
        return parsed;
      }
    } catch { /* ignore */ }
    const defaultLayout = getDefaultLayout(project.projectType);
    return { lg: defaultLayout.filter((l) => visibleWidgets.includes(l.i)) };
  });

  const handleLayoutChange = useCallback((_: unknown, allLayouts: Record<string, unknown>) => {
    setLayouts(allLayouts);
    try {
      localStorage.setItem(getStorageKey(project.id), JSON.stringify(allLayouts));
    } catch { /* ignore */ }
  }, [project.id]);

  const handleRefresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['dashboard', project.id] });
  };

  const handleAddWidget = (widgetId: string) => {
    const updated = [...visibleWidgets, widgetId];
    setVisibleWidgets(updated);
    localStorage.setItem(getWidgetStorageKey(project.id), JSON.stringify(updated));
    setShowAddMenu(false);

    // 새 레이아웃 항목 추가
    const def = availableWidgets.find((w) => w.id === widgetId);
    if (def) {
      const currentLg = (layouts.lg ?? []) as Array<{ i: string; x: number; y: number; w: number; h: number }>;
      const maxY = currentLg.reduce((m, l) => Math.max(m, l.y + l.h), 0);
      const newItem = { i: widgetId, x: 0, y: maxY, w: def.defaultW, h: def.defaultH, minW: 1, minH: 2 };
      const newLayouts = { ...layouts, lg: [...currentLg, newItem] };
      setLayouts(newLayouts);
      localStorage.setItem(getStorageKey(project.id), JSON.stringify(newLayouts));
    }
  };

  const handleRemoveWidget = (widgetId: string) => {
    const updated = visibleWidgets.filter((w) => w !== widgetId);
    setVisibleWidgets(updated);
    localStorage.setItem(getWidgetStorageKey(project.id), JSON.stringify(updated));
  };

  const hiddenWidgets = availableWidgets.filter((w) => !visibleWidgets.includes(w.id));

  const renderWidget = (widgetId: string) => {
    switch (widgetId) {
      case 'status-pie':
        return <IssueStatusPieWidget projectId={project.id} projectKey={project.projectKey} />;
      case 'created-vs-resolved':
        return <CreatedVsResolvedWidget projectId={project.id} projectCreatedAt={project.createdAt} />;
      case 'priority-bar':
        return <PriorityBarWidget projectId={project.id} />;
      case 'assignee-workload':
        return <AssigneeWorkloadWidget projectId={project.id} />;
      case 'burndown':
        return <BurndownWidget projectId={project.id} projectType={project.projectType} />;
      case 'test-execution':
        return <TestExecutionDonutWidget projectId={project.id} />;
      case 'req-coverage':
        return <RequirementCoverageWidget projectId={project.id} />;
      case 'activity':
        return <RecentActivityWidget projectId={project.id} projectKey={project.projectKey} />;
      default:
        return null;
    }
  };

  return (
    <div>
      {/* 헤더 바 */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-gray-900">대시보드</h1>
        <div className="flex items-center gap-2">
          {/* 새로고침 */}
          <button
            onClick={handleRefresh}
            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            title="새로고침"
          >
            <RefreshCw className="size-4" />
          </button>

          {/* 위젯 추가 */}
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              disabled={hiddenWidgets.length === 0}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40 transition-colors"
            >
              <Plus className="size-3.5" />
              위젯 추가
            </button>
            {showAddMenu && hiddenWidgets.length > 0 && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-30">
                {hiddenWidgets.map((w) => (
                  <button
                    key={w.id}
                    onClick={() => handleAddWidget(w.id)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {w.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 그리드 레이아웃 */}
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 3, md: 3, sm: 2, xs: 1, xxs: 1 }}
        rowHeight={140}
        draggableHandle=".widget-drag-handle"
        onLayoutChange={handleLayoutChange}
        compactType="vertical"
        margin={[16, 16]}
      >
        {visibleWidgets.map((widgetId) => (
          <div key={widgetId} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* 드래그 핸들 + 삭제 버튼 */}
            <div className="widget-drag-handle flex items-center justify-between px-3 py-1 bg-gray-50 border-b border-gray-100 cursor-move">
              <span className="text-[11px] text-gray-400 select-none">⠿ 드래그</span>
              <button
                onClick={() => handleRemoveWidget(widgetId)}
                className="p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors"
                title="위젯 제거"
              >
                <X className="size-3" />
              </button>
            </div>
            <div className="p-3 h-[calc(100%-28px)]">
              {renderWidget(widgetId)}
            </div>
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  );
}
